import { supabase } from './supabaseService';
import { serviceConnectionsService } from './serviceConnectionsService';
import { logger } from '../utils/logger';
import {
  FathomConfig,
  FathomMeeting,
  FathomListResponse,
  FathomAnalytics,
  FathomTranscriptItem,
  FathomMeetingSummary,
  FathomWebhook,
  FathomWebhookCreatePayload
} from '../types/fathom';

class FathomService {
  private config: FathomConfig | null = null;
  private rateLimitedUntil: number = 0;
  private requestCache: Map<string, { data: any; timestamp: number }> = new Map();
  private readonly cacheTimeout = 60000; // 1 min
  private configLoadedFromDb = false;

  // ----------------------------------------------------------------
  // Rate-limit & cache helpers
  // ----------------------------------------------------------------

  private isRateLimited(): boolean {
    return Date.now() < this.rateLimitedUntil;
  }

  getRateLimitStatus(): { isLimited: boolean; remainingTime: number } {
    const isLimited = this.isRateLimited();
    const remainingTime = isLimited ? Math.ceil((this.rateLimitedUntil - Date.now()) / 1000) : 0;
    return { isLimited, remainingTime };
  }

  clearRateLimit(): void {
    this.rateLimitedUntil = 0;
  }

  clearCache(): void {
    this.requestCache.clear();
  }

  private getCached(key: string): any | null {
    const cached = this.requestCache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }
    return null;
  }

  private setCache(key: string, data: any): void {
    this.requestCache.set(key, { data, timestamp: Date.now() });
    if (this.requestCache.size > 100) {
      const oldest = this.requestCache.keys().next().value;
      if (oldest) this.requestCache.delete(oldest);
    }
  }

  // ----------------------------------------------------------------
  // Configuration
  // ----------------------------------------------------------------

  /** Check if Fathom is configured (prefers DB, falls back to localStorage) */
  isConfigured(): boolean {
    const config = this.config || this.loadConfigFromLocalStorage();
    return !!(config?.apiKey && config.apiKey.trim().length > 10 && config.isConfigured);
  }

  /** Load config from localStorage (legacy, fast, synchronous) */
  loadConfig(): FathomConfig | null {
    return this.loadConfigFromLocalStorage();
  }

  private loadConfigFromLocalStorage(): FathomConfig | null {
    try {
      const raw = localStorage.getItem('crm_configuration');
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      if (!parsed.fathomApiKey) return null;

      const config: FathomConfig = {
        apiKey: parsed.fathomApiKey || '',
        webhookSecret: parsed.fathomWebhookSecret || '',
        isConfigured: parsed.fathomIsConfigured || false,
        webhookUrl: parsed.fathomWebhookUrl || '',
        lastSync: parsed.fathomLastSync ? new Date(parsed.fathomLastSync) : undefined
      };

      this.config = config;
      return config;
    } catch {
      return null;
    }
  }

  /** Load config from Supabase service_connections table */
  async loadConfigFromDatabase(): Promise<FathomConfig | null> {
    try {
      const connection = await serviceConnectionsService.getServiceConnection('fathom');
      if (!connection) return null;

      const config: FathomConfig = {
        apiKey: connection.apiKey || '',
        webhookSecret: connection.configData?.webhookSecret || '',
        isConfigured: connection.isActive,
        webhookUrl: connection.configData?.webhookUrl || '',
        lastSync: connection.lastConnectedAt
      };

      this.config = config;
      this.configLoadedFromDb = true;
      return config;
    } catch (error) {
      logger.error('Error loading Fathom config from database', error);
      return null;
    }
  }

  /** Save config to localStorage (legacy) */
  saveConfig(updates: Partial<FathomConfig>): void {
    try {
      const raw = localStorage.getItem('crm_configuration');
      const current = raw ? JSON.parse(raw) : {};

      if (updates.apiKey !== undefined) current.fathomApiKey = updates.apiKey;
      if (updates.webhookSecret !== undefined) current.fathomWebhookSecret = updates.webhookSecret;
      if (updates.isConfigured !== undefined) current.fathomIsConfigured = updates.isConfigured;
      if (updates.webhookUrl !== undefined) current.fathomWebhookUrl = updates.webhookUrl;
      if (updates.lastSync !== undefined) current.fathomLastSync = updates.lastSync?.toISOString();

      localStorage.setItem('crm_configuration', JSON.stringify(current));

      this.config = { ...(this.config || { apiKey: '', isConfigured: false }), ...updates };

      // Also persist to DB
      this.saveConfigAsync(updates).catch(err =>
        logger.error('Error persisting Fathom config to DB', err)
      );
    } catch (error) {
      logger.error('Error saving Fathom config', error);
      throw error;
    }
  }

  /** Save config to Supabase (async, primary store) */
  async saveConfigAsync(updates: Partial<FathomConfig>): Promise<void> {
    const current = this.config || { apiKey: '', isConfigured: false };
    const merged = { ...current, ...updates };

    await serviceConnectionsService.saveServiceConnection(
      'fathom',
      merged.apiKey,
      {
        webhookSecret: merged.webhookSecret,
        webhookUrl: merged.webhookUrl,
        isConfigured: merged.isConfigured
      }
    );

    this.config = merged;
  }

  // ----------------------------------------------------------------
  // HTTP — all requests go through the Supabase Edge Function proxy
  // ----------------------------------------------------------------

  private async makeRequest(
    endpoint: string,
    method: 'GET' | 'POST' | 'DELETE' = 'GET',
    body?: any,
    retryCount: number = 0
  ): Promise<any> {
    if (!this.isConfigured()) {
      throw new Error('Fathom no está configurado. Configura tu API Key en Ajustes.');
    }

    if (this.isRateLimited()) {
      const wait = Math.ceil((this.rateLimitedUntil - Date.now()) / 1000);
      throw new Error(`Límite de API alcanzado. Espera ${wait} segundos.`);
    }

    // Check cache for GET
    if (method === 'GET') {
      const cached = this.getCached(`GET:${endpoint}`);
      if (cached) return cached;
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('No autenticado');

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const proxyUrl = new URL(`${supabaseUrl}/functions/v1/fathom-proxy`);
    proxyUrl.searchParams.set('endpoint', endpoint);
    proxyUrl.searchParams.set('method', method);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    try {
      const response = await fetch(proxyUrl.toString(), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      // Handle rate-limiting
      if (response.status === 429) {
        const resetHeader = response.headers.get('X-RateLimit-Reset');
        const waitMs = resetHeader ? parseInt(resetHeader) * 1000 : 60000;
        this.rateLimitedUntil = Date.now() + waitMs;
        throw new Error('Límite de 60 llamadas/minuto alcanzado. Espera un momento.');
      }

      const contentType = response.headers.get('content-type');
      let data;
      if (contentType?.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        data = { error: text || 'Respuesta no JSON', status: response.status };
      }

      if (!response.ok) {
        const msg = data?.error || data?.message || `HTTP ${response.status}`;
        if (response.status === 401) throw new Error('Credenciales inválidas. Verifica tu API Key de Fathom.');
        if (response.status === 403) throw new Error('Permisos insuficientes en Fathom.');
        if (response.status >= 500 && retryCount < 2) {
          await new Promise(r => setTimeout(r, 1000 * (retryCount + 1)));
          return this.makeRequest(endpoint, method, body, retryCount + 1);
        }
        throw new Error(`Error Fathom API: ${msg}`);
      }

      // Cache GET responses
      if (method === 'GET') {
        this.setCache(`GET:${endpoint}`, data);
      }

      return data;
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Timeout: Fathom no responde (30s).');
      }
      throw error;
    }
  }

  // ----------------------------------------------------------------
  // Public API methods
  // ----------------------------------------------------------------

  /** Test the connection by fetching 1 meeting */
  async testConnection(): Promise<boolean> {
    try {
      const response = await this.makeRequest('/meetings?created_after=2020-01-01T00:00:00Z');
      if (!response || !response.items) {
        throw new Error('Respuesta inesperada de Fathom');
      }
      this.saveConfig({ isConfigured: true, lastSync: new Date() });
      return true;
    } catch (error: any) {
      this.saveConfig({ isConfigured: false });
      throw error;
    }
  }

  /** GET /meetings — returns paginated list */
  async getMeetings(limit: number = 20, _offset?: number, cursor?: string): Promise<FathomMeeting[]> {
    try {
      let endpoint = `/meetings?include_summary=true&include_action_items=true`;
      if (cursor) {
        endpoint += `&cursor=${encodeURIComponent(cursor)}`;
      }

      const result: FathomListResponse<FathomMeeting> = await this.makeRequest(endpoint);

      if (!result?.items || !Array.isArray(result.items)) {
        logger.warn('Fathom /meetings returned unexpected format', result);
        return [];
      }

      logger.info(`Fathom: received ${result.items.length} meetings`);
      return result.items;
    } catch (error) {
      logger.error('Error getting Fathom meetings', error);
      return [];
    }
  }

  /** GET /meetings with full pagination (returns raw response including next_cursor) */
  async getMeetingsPage(cursor?: string): Promise<FathomListResponse<FathomMeeting>> {
    const endpoint = cursor
      ? `/meetings?include_summary=true&include_action_items=true&cursor=${encodeURIComponent(cursor)}`
      : '/meetings?include_summary=true&include_action_items=true';

    return await this.makeRequest(endpoint);
  }

  /** Get a single meeting by recording_id */
  async getMeeting(recordingId: string): Promise<FathomMeeting | null> {
    try {
      // The API doesn't have a single-meeting endpoint, so we search
      const meetings = await this.getMeetings();
      return meetings.find(m => String(m.recording_id) === recordingId) || null;
    } catch (error) {
      logger.error('Error getting Fathom meeting', error);
      return null;
    }
  }

  /** GET /recordings/:id/transcript */
  async getTranscript(recordingId: string): Promise<FathomTranscriptItem[]> {
    try {
      const result = await this.makeRequest(`/recordings/${recordingId}/transcript`);
      return result?.transcript || result || [];
    } catch (error) {
      logger.error('Error getting Fathom transcript', error);
      throw error;
    }
  }

  /** GET /recordings/:id/summary */
  async getSummary(recordingId: string): Promise<FathomMeetingSummary | null> {
    try {
      const result = await this.makeRequest(`/recordings/${recordingId}/summary`);
      return result?.summary || result || null;
    } catch (error) {
      logger.error('Error getting Fathom summary', error);
      throw error;
    }
  }

  /** Alias for backwards-compat with recordingProviderManager */
  async getRecording(recordingId: string): Promise<any> {
    return this.getMeeting(recordingId);
  }

  // ----------------------------------------------------------------
  // Webhooks
  // ----------------------------------------------------------------

  async createWebhook(payload: FathomWebhookCreatePayload): Promise<FathomWebhook> {
    return await this.makeRequest('/webhooks', 'POST', payload);
  }

  async deleteWebhook(webhookId: string): Promise<void> {
    await this.makeRequest(`/webhooks/${webhookId}`, 'DELETE');
  }

  // ----------------------------------------------------------------
  // Analytics (computed client-side from meeting data)
  // ----------------------------------------------------------------

  calculateAnalytics(meetings: FathomMeeting[]): FathomAnalytics {
    if (!Array.isArray(meetings) || meetings.length === 0) {
      return this.getMeetingStats();
    }

    const valid = meetings.filter(m => m && m.recording_id);

    let totalDuration = 0;
    let withSummary = 0;
    let withActionItems = 0;
    let totalActionItems = 0;
    let completedActionItems = 0;
    let internal = 0;
    let external = 0;

    for (const m of valid) {
      // Duration from recording timestamps
      if (m.recording_start_time && m.recording_end_time) {
        const start = new Date(m.recording_start_time).getTime();
        const end = new Date(m.recording_end_time).getTime();
        if (end > start) totalDuration += (end - start) / 1000;
      }

      if (m.default_summary?.markdown_formatted) withSummary++;

      if (m.action_items && m.action_items.length > 0) {
        withActionItems++;
        totalActionItems += m.action_items.length;
        completedActionItems += m.action_items.filter(ai => ai.completed).length;
      }

      if (m.calendar_invitees_domains_type === 'one_or_more_external') {
        external++;
      } else {
        internal++;
      }
    }

    return {
      total_meetings: valid.length,
      internal_meetings: internal,
      external_meetings: external,
      total_duration_seconds: Math.round(totalDuration),
      average_duration_seconds: valid.length > 0 ? Math.round(totalDuration / valid.length) : 0,
      meetings_with_summary: withSummary,
      meetings_with_action_items: withActionItems,
      total_action_items: totalActionItems,
      completed_action_items: completedActionItems,
      last_sync: this.config?.lastSync || new Date()
    };
  }

  getMeetingStats(): FathomAnalytics {
    return {
      total_meetings: 0,
      internal_meetings: 0,
      external_meetings: 0,
      total_duration_seconds: 0,
      average_duration_seconds: 0,
      meetings_with_summary: 0,
      meetings_with_action_items: 0,
      total_action_items: 0,
      completed_action_items: 0,
      last_sync: this.config?.lastSync || undefined
    };
  }

  /** Alias used by recordingProviderManager */
  async getAnalytics(): Promise<FathomAnalytics> {
    const meetings = await this.getMeetings();
    return this.calculateAnalytics(meetings);
  }
}

export const fathomService = new FathomService();
